import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useTheme } from '@chakra-ui/react'
import { useVirtualizer, type VirtualItem, type Virtualizer } from '@tanstack/react-virtual'

import { useConfig } from 'src/contexts/ConfigurationContext'
import { getTranscriptFontSize } from 'src/models/UIConfiguration'
import { useFerretSearch } from 'src/contexts/FerretSearchWrapperProvider'
import { useTranscriptClient, useTranscriptParagraphIds, useTranscriptLocalUserState } from 'src/contexts/Transcript/TranscriptContext'
import { useTranscriptViewer } from 'src/contexts/TranscriptViewerProvider'
import { usePrevious } from 'src/hooks/usePrevious'
import { useResponsiveContainer } from 'src/contexts/ResponsiveContainerContext'
import { useFeatureFlag } from 'src/contexts/FeatureFlagContext'
import { useEditor } from 'src/contexts/EditorContext'
import { useGlobalKeydownEvent } from 'src/hooks/useGlobalEvent'
import { HOTKEYS } from 'src/utils/hotkeys'
import { QuickActionMenu } from 'src/components/QuickActionMenu/QuickActionMenu'
import { QuickActionFerretSearch, QuickActionBoostTerm } from 'src/components/QuickActionMenu/actions'

import { getInconsistencyIndicatorScale } from './constants'
import { ParagraphView, getContentHeight, getMetadataHeight, getParagraphHeight, getParagraphMaxWidth } from './ParagraphView'
import { useInconsistencyGutter } from './ParagraphView/useInconsistencyGutter'
import { ScrollButton } from './ScrollButton'
import { ATTENTION_PULSE_DURATION_MS, handleScrollButtonClick } from './scrollButtonClickActions'
import { shouldAdjustVirtualizerScrollOnItemSizeChange } from './shouldAdjustVirtualizerScrollOnItemSizeChange'
import * as S from './styles'
import { useMediaPlaybackFollow } from './hooks/useMediaPlaybackFollow'
import { useSeekOnClick } from './hooks/useSeekOnClick'
import { usePlaybackHighlight, useTranscriptHighlight } from './hooks/useTranscriptHighlight'

type TranscriptViewerProps = {
    onInitialFontAvailable: () => void
}

export const TranscriptViewer = (props: TranscriptViewerProps) => {
    const { onInitialFontAvailable } = props

    const [{ fontSizePercentage, shouldDisplayLogo, isFullWidthMode }] = useConfig()
    const fontSize = getTranscriptFontSize(fontSizePercentage)
    const {
        fontFamily,
        textColorIndex,
        palette: {
            transcript: { textColors },
        },
    } = useTheme()
    const logoColor = textColors[textColorIndex].colorValue
    const { isMediumContainer, isSmallContainer, containerWidth } = useResponsiveContainer()

    const {
        listRef,
        listScrollingContainerRef,
        autoScrollMode,
        hasNewContent,
        initManualScrollingSideEffects,
        enterAutoScroll,
        exitAutoScroll,
        pauseAutoScroll,
    } = useTranscriptViewer()
    const { shouldShowFerretSearch } = useFerretSearch()
    const transcriptClient = useTranscriptClient()
    const isTermBoostingEnabled = useFeatureFlag('term_boosting')
    const { isTranscriptEditAuthorised } = useEditor()

    const inconsistencyGutter = useInconsistencyGutter(containerWidth)
    const metadataHeight = useMemo(() => getMetadataHeight({ fontSize, isMobileLayout: isMediumContainer }), [fontSize, isMediumContainer])
    const maxWidth = useMemo(
        () =>
            getParagraphMaxWidth({
                containerWidth,
                shouldDisplayLogo: shouldDisplayLogo && !isSmallContainer,
                isMobilePhone: isSmallContainer,
                isFullWidthMode,
                inconsistencyGutter,
            }),
        [containerWidth, inconsistencyGutter, isFullWidthMode, isSmallContainer, shouldDisplayLogo],
    )
    const estimateParagraphHeight = useCallback(
        (index: number) => {
            const paragraph = transcriptClient.getParagraphByIndex(index)

            return getParagraphHeight({
                paragraph,
                fontSize,
                isMobileLayout: isMediumContainer,
                maxWidth,
                metadataHeight,
            })
        },
        [fontSize, isMediumContainer, maxWidth, metadataHeight, transcriptClient],
    )

    const paragraphIds = useTranscriptParagraphIds()
    const totalParagraphCount = transcriptClient.getTotalParagraphCount()
    const isDiarizationSupported = transcriptClient.isDiarizationSupported()

    // `useVirtualizer` is sensitive to options identity; recreating option callbacks/objects every render
    // caused a dev-only "Too many re-renders" loop locally, so we keep these references stable.
    const getScrollElement = useCallback(() => listScrollingContainerRef.current, [listScrollingContainerRef])
    const getItemKey = useCallback((index: number) => `${index}-${paragraphIds[index]}`, [paragraphIds])
    const shouldAdjustScrollPositionOnItemSizeChange = useCallback(
        (item: VirtualItem, _delta: number, instance: Virtualizer<HTMLDivElement, Element>) =>
            shouldAdjustVirtualizerScrollOnItemSizeChange(item, totalParagraphCount - 1, instance.scrollOffset ?? 0),
        [totalParagraphCount],
    )
    const { onListLayoutChange } = useTranscriptHighlight()
    const virtualizerOptions = useMemo(
        () => ({
            count: totalParagraphCount,
            getScrollElement,
            estimateSize: estimateParagraphHeight,
            getItemKey,
            enabled: true,
            onChange: onListLayoutChange,
        }),
        [estimateParagraphHeight, getItemKey, getScrollElement, onListLayoutChange, totalParagraphCount],
    )

    const virtualizer = useVirtualizer(virtualizerOptions)
    // Instance field, not VirtualizerOptions — passing it into useVirtualizer is ignored
    // (https://github.com/TanStack/virtual/issues/1227).
    virtualizer.shouldAdjustScrollPositionOnItemSizeChange = shouldAdjustScrollPositionOnItemSizeChange
    listRef.current = virtualizer
    const items = virtualizer.getVirtualItems()

    usePlaybackHighlight()

    const previousTranscriptHeight = useRef(0)
    const estimatedTranscriptHeight = listRef.current.getTotalSize()

    // when transcript height _increases_ (e.g. as new content is added), we always use actual (estimated) height;
    // when transcript height decreases _slightly_ (no more than 3 lines - e.g. due to ASR self-correction), we
    // keep the previous height to avoid "bouncyness";
    // when transcript height decreases _significantly_ (e.g. due to font size decrease or full width mode toggle),
    // we use actual height again, to avoid large blank spaces
    const threshold = useMemo(() => getContentHeight({ fontSize, linesAmount: 3 }), [fontSize])
    const delta = estimatedTranscriptHeight - previousTranscriptHeight.current
    const actualTranscriptHeight = delta < 0 && delta > -threshold ? previousTranscriptHeight.current : estimatedTranscriptHeight

    previousTranscriptHeight.current = actualTranscriptHeight

    useEffect(() => {
        if (document.activeElement === document.body) {
            listScrollingContainerRef.current?.focus()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const scrollToEnd = useCallback(() => {
        listRef.current?.scrollToIndex(totalParagraphCount)
    }, [listRef, totalParagraphCount])

    const [isAttentionPulsing, setIsAttentionPulsing] = useState(false)

    const onScrollButtonClick = useCallback(() => {
        handleScrollButtonClick({
            autoScrollMode,
            pauseAutoScroll,
            enterAutoScroll,
            startAttentionPulse: () => setIsAttentionPulsing(true),
        })
    }, [autoScrollMode, enterAutoScroll, pauseAutoScroll])

    useEffect(() => {
        if (!isAttentionPulsing) {
            return
        }

        const timer = setTimeout(() => setIsAttentionPulsing(false), ATTENTION_PULSE_DURATION_MS)

        return () => clearTimeout(timer)
    }, [isAttentionPulsing])

    useMediaPlaybackFollow(listRef)
    const seekOnClick = useSeekOnClick()

    const isInitialListRefChange = useRef(true)
    useEffect(() => {
        document.fonts.ready
            .then((fontFaceSet) => {
                // even though `document.fonts.ready` promise is resolved, additional fonts for transcript customization
                // might not be loaded yet, cause they aren't used in styles initially;
                // so, here we're looking for a specific font face:
                //  - family equal to active font family;
                //  - regular weight;
                //  - Unicode range is https://en.wikipedia.org/wiki/Plane_(Unicode)#Basic_Multilingual_Plane
                // this is important, cause additional weights and ranges are not used in transcript,
                // and therefore will not be requested by browser, so their status will never change
                const targetFaces = [...fontFaceSet].filter(
                    ({ family, weight, unicodeRange }) => family === fontFamily && weight === '400' && unicodeRange === 'U+0-10FFFF',
                )

                return Promise.all(targetFaces.map((fontFace) => fontFace.load()))
            })
            .then(() => {
                // after we got the font, scroll to the bottom of the list
                // (no need to scroll down on subsequent font family changes)
                if (isInitialListRefChange.current) {
                    scrollToEnd()
                    isInitialListRefChange.current = false
                }
                onInitialFontAvailable()
            })
        // the effect should only happen when font family is changed
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fontFamily])

    const prevFontSize = usePrevious(fontSize)
    useEffect(() => {
        const container = listScrollingContainerRef.current
        const shouldScrollDown = !!container && !!prevFontSize && fontSize !== prevFontSize && autoScrollMode
        if (shouldScrollDown) {
            enterAutoScroll({ shouldFocusContainer: false })
        }
    }, [autoScrollMode, enterAutoScroll, fontSize, listScrollingContainerRef, prevFontSize])

    useEffect(() => {
        const dispose = initManualScrollingSideEffects()

        return () => {
            dispose()
        }
    }, [initManualScrollingSideEffects])

    const localUserState = useTranscriptLocalUserState()
    const textEditingParagraphId = localUserState.selection?.paragraphId
    const previousTextEditingParagraphId = usePrevious(textEditingParagraphId)
    useEffect(() => {
        // stop the autoscroll when user starts editing
        if (!!textEditingParagraphId && textEditingParagraphId !== previousTextEditingParagraphId) {
            exitAutoScroll()
        }
    }, [exitAutoScroll, previousTextEditingParagraphId, textEditingParagraphId])

    const onKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (!isTranscriptEditAuthorised) {
                return
            }

            if (HOTKEYS.undo.check(event)) {
                event.preventDefault()
                transcriptClient.undo()
            }
            if (HOTKEYS.redo.check(event)) {
                event.preventDefault()
                transcriptClient.redo()
            }
        },
        [isTranscriptEditAuthorised, transcriptClient],
    )

    const onToggleAutoscrollHotkey = useCallback(
        (event: KeyboardEvent) => {
            if (!HOTKEYS.toggleAutoscroll.check(event) || event.repeat) {
                return
            }

            event.preventDefault()
            onScrollButtonClick()
        },
        [onScrollButtonClick],
    )
    useGlobalKeydownEvent(onToggleAutoscrollHotkey)

    return (
        <>
            {shouldDisplayLogo && <S.StyledLogo $color={logoColor} />}
            <S.Container
                $fontSize={fontSize}
                $inconsistencyIndicatorScale={getInconsistencyIndicatorScale(fontSizePercentage)}
                ref={listScrollingContainerRef}
                onKeyDown={onKeyDown}
                onMouseUp={seekOnClick}
                tabIndex={0}>
                <S.ScrollingContainer style={{ height: actualTranscriptHeight }}>
                    <S.Scroller style={{ transform: `translateY(${items[0]?.start ?? 0}px)` }}>
                        {items.map((virtualRow) => (
                            <ParagraphView
                                key={virtualRow.key}
                                ref={listRef.current?.measureElement}
                                index={virtualRow.index}
                                paragraphId={paragraphIds[virtualRow.index]}
                                isFirstParagraph={virtualRow.index === 0}
                                previousParagraphId={paragraphIds[virtualRow.index - 1]}
                                containerWidth={containerWidth}
                                isDiarizationSupported={isDiarizationSupported}
                            />
                        ))}
                    </S.Scroller>
                </S.ScrollingContainer>
                <QuickActionMenu containerWidth={containerWidth} outerElementRef={listScrollingContainerRef}>
                    {shouldShowFerretSearch && <QuickActionFerretSearch />}
                    {isTermBoostingEnabled && <QuickActionBoostTerm />}
                </QuickActionMenu>
            </S.Container>
            <ScrollButton
                onClick={onScrollButtonClick}
                autoScrollMode={autoScrollMode}
                hasIndicator={hasNewContent}
                isAttentionPulsing={isAttentionPulsing}
            />
        </>
    )
}
